import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Auth Components
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';

// Layouts
import MainLayout from './components/layouts/MainLayout';

// Pages
import Dashboard from './pages/Dashboard';
import DailyPlan from './pages/DailyPlan';
import CalendarPage from './pages/Calendar';
import PomodoroTV from './pages/PomodoroTV';
import Reports from './pages/Reports';
import BoardView from './pages/BoardView';
import CreateBoard from './pages/CreateBoard';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Teams from './pages/Teams';
import VerificationSent from './pages/VerificationSent';
import VerificationSuccess from './pages/VerificationSuccess';
import PublicForm from './pages/PublicForm';
import RequestUpdate from './pages/RequestUpdate';
import SharedRequest from './pages/SharedRequest';

// Set base API URL
import axios from 'axios';
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Mind maps are lazy-loaded so React Flow stays out of the main bundle.
const MindMaps = React.lazy(() => import('./pages/MindMaps'));
const MindMapEditor = React.lazy(() => import('./pages/MindMapEditor'));
const FlowMaps = React.lazy(() => import('./pages/FlowMaps'));
const FlowMapEditor = React.lazy(() => import('./pages/FlowMapEditor'));
const CardFlowView = React.lazy(() => import('./pages/CardFlowView'));

// Google Ads pages are lazy-loaded so recharts stays out of the main bundle.
const AdsDashboard = React.lazy(() => import('./pages/ads/AdsDashboard'));
const AdsCampaigns = React.lazy(() => import('./pages/ads/AdsCampaigns'));
const AdsCampaignDetail = React.lazy(() => import('./pages/ads/AdsCampaignDetail'));
const AdsCampaignCreate = React.lazy(() => import('./pages/ads/AdsCampaignCreate'));
const AdsSearchTerms = React.lazy(() => import('./pages/ads/AdsSearchTerms'));
const AdsPlacements = React.lazy(() => import('./pages/ads/AdsPlacements'));
const AdsBudgets = React.lazy(() => import('./pages/ads/AdsBudgets'));
const AdsReports = React.lazy(() => import('./pages/ads/AdsReports'));
const AdsChangeLog = React.lazy(() => import('./pages/ads/AdsChangeLog'));
const Campaigns = React.lazy(() => import('./pages/Campaigns'));
const OrgChart = React.lazy(() => import('./pages/OrgChart'));
const TeamGoals = React.lazy(() => import('./pages/TeamGoals'));
const Forms = React.lazy(() => import('./pages/Forms'));
const CampaignDetail = React.lazy(() => import('./pages/CampaignDetail'));

const Lazy = ({ children }) => (
  <React.Suspense fallback={
    <div className="flex items-center justify-center h-[50vh]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-blue" />
    </div>
  }>
    {children}
  </React.Suspense>
);

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <SocketProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/verification-sent" element={<VerificationSent />} />
          <Route path="/verification-success" element={<VerificationSuccess />} />
          {/* Public intake forms — anyone with the link, no login. */}
          <Route path="/f/:slug" element={<PublicForm />} />
          {/* A submitter's private page to comment on / add files to their request. */}
          <Route path="/u/:token" element={<RequestUpdate />} />
          {/* Unlisted card pages shared outside the tracker. */}
          <Route path="/r/:token" element={<SharedRequest />} />

          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            {/* Chrome-free fullscreen view for an office TV */}
            <Route path="/tv" element={<PomodoroTV />} />
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/daily-plan" element={<DailyPlan />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/boards/new" element={<CreateBoard />} />
              <Route path="/boards/:boardIdOrSlug" element={<BoardView />} />
              <Route path="/mindmaps" element={<Lazy><MindMaps /></Lazy>} />
              <Route path="/maps/:mapId" element={<Lazy><MindMapEditor /></Lazy>} />
              <Route path="/flowmaps" element={<Lazy><FlowMaps /></Lazy>} />
              <Route path="/flows/:mapId" element={<Lazy><FlowMapEditor /></Lazy>} />
              <Route path="/boards/:boardIdOrSlug/flow" element={<Lazy><CardFlowView /></Lazy>} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/admin" element={<AdminPanel />} />
              {/* Google Ads. "/new" is declared before ":campaignId" so the
                  dynamic segment doesn't swallow it. */}
              <Route path="/ads" element={<Lazy><AdsDashboard /></Lazy>} />
              <Route path="/ads/campaigns" element={<Lazy><AdsCampaigns /></Lazy>} />
              <Route path="/ads/campaigns/new" element={<Lazy><AdsCampaignCreate /></Lazy>} />
              <Route path="/ads/campaigns/:campaignId" element={<Lazy><AdsCampaignDetail /></Lazy>} />
              <Route path="/ads/search-terms" element={<Lazy><AdsSearchTerms /></Lazy>} />
              <Route path="/ads/placements" element={<Lazy><AdsPlacements /></Lazy>} />
              <Route path="/ads/budgets" element={<Lazy><AdsBudgets /></Lazy>} />
              <Route path="/ads/reports" element={<Lazy><AdsReports /></Lazy>} />
              <Route path="/ads/changes" element={<Lazy><AdsChangeLog /></Lazy>} />
              {/* Campaign Builder (admin-only, enforced by the API) */}
              <Route path="/campaigns" element={<Lazy><Campaigns /></Lazy>} />
              <Route path="/org-chart" element={<Lazy><OrgChart /></Lazy>} />
              <Route path="/team-goals" element={<Lazy><TeamGoals /></Lazy>} />
              {/* Intake form manager (admin-only, enforced by the API) */}
              <Route path="/forms" element={<Lazy><Forms /></Lazy>} />
              <Route path="/campaigns/:id" element={<Lazy><CampaignDetail /></Lazy>} />
              {/* Add more routes here as they are developed */}
            </Route>
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Router>
      </SocketProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;